// Required exact dimensions for menu-card images.
export const REQUIRED_WIDTH = 960;
export const REQUIRED_HEIGHT = 640;

// The two platforms a region maintains independent card lists for.
export const PLATFORMS = [
  { id: "web", label: "Web" },
  { id: "mobile", label: "Mobile" },
];

// Stable local id for keying regions/cards in react-beautiful-dnd before the
// backend assigns real ids. Browser-only; never persisted.
let counter = 0;
export const uid = (prefix = "id") =>
  `${prefix}_${Date.now().toString(36)}_${(counter++).toString(36)}`;

// App deep-link schemes the backend converts for iOS on the mobile (menu_images)
// list. Allowed only when validating mobile cards.
export const DEEP_LINK_PROTOCOLS = ["saucey:", "sauceydev:"];

// URL check for the optional card link. http(s) is always allowed; the saucey://
// deep-link schemes are allowed only when allowDeepLinks is set (mobile cards).
export const isValidUrl = (value, allowDeepLinks = false) => {
  if (!value) return true; // link is optional
  try {
    const u = new URL(value);
    if (u.protocol === "http:" || u.protocol === "https:") return true;
    if (allowDeepLinks && DEEP_LINK_PROTOCOLS.includes(u.protocol)) return true;
    return false;
  } catch {
    return false;
  }
};

// Normalize the city list into a consistent { id, name, state } shape, preserving
// the legacy web_images/menu_images carried on each city (used to seed regions).
// Tolerates the backend returning either bare strings or objects.
export const normalizeCities = (cities = []) =>
  (Array.isArray(cities) ? cities : []).map((c) =>
    typeof c === "string"
      ? { id: c, name: c, state: undefined, web_images: [], menu_images: [] }
      : {
          id: c.id ?? c._id ?? c.slug ?? c.name,
          name: c.name ?? c.id,
          state: c.state,
          web_images: c.web_images ?? [],
          menu_images: c.menu_images ?? [],
        }
  );

// Normalize regions coming from the server into the FE working shape, stamping
// stable local ids and guaranteeing web/mobile/cities arrays exist.
export const normalizeRegions = (regions = []) =>
  (Array.isArray(regions) ? regions : []).map((r) => ({
    _localId: uid("region"),
    id: r.id ?? r._id ?? null,
    name: r.name ?? "",
    default: Boolean(r.default), // global fallback when no city matches

    cities: Array.isArray(r.cities)
      ? r.cities.map((c) => (typeof c === "string" ? c : c.id ?? c._id ?? c.name))
      : [],
    // API exposes web_images/mobile_images; FE works with web/mobile internally.
    web: normalizeCards(r.web_images),
    mobile: normalizeCards(r.menu_images),
  }));

export const normalizeCards = (cards = []) =>
  (Array.isArray(cards) ? cards : [])
    .filter((c) => c && c.image) // images only for now (video out of scope)
    .map((c) => ({ _localId: uid("card"), image: c.image, link: c.link ?? "" }));

// Strip FE-only fields before POSTing to the backend.
export const serializeRegions = (regions = []) =>
  regions.map((r) => ({
    ...(r.id ? { id: r.id } : {}),
    name: r.name,
    default: Boolean(r.default),
    cities: r.cities,
    web_images: r.web.map(({ image, link }) => ({ image, link })),
    menu_images: r.mobile.map(({ image, link }) => ({ image, link })),
  }));
